import { type JSX, type ReactNode, useState } from "react";

import Banner from "common/components/subscriptions/Banner";
import QuantitySelector from "common/components/subscriptions/QuantitySelector";
import ToPayNow from "common/components/subscriptions/ToPayNow";
import { Button } from "common/components/ui/Button";
import Skeleton from "common/components/ui/Skeleton";
import useRentalMutations from "common/datahooks/useRentalMutations";
import useSubscriptionMutations from "common/datahooks/useSubscriptionMutations";
import useWorkspaceEstimateCreation from "common/datahooks/useWorkspaceEstimateCreation";
import useWorkspaceEstimateRenewal from "common/datahooks/useWorkspaceEstimateRenewal";
import useWorkspaceEstimateUpdate from "common/datahooks/useWorkspaceEstimateUpdate";
import { useSelectedWorkspaceContext } from "common/helpers/SelectedWorkspaceContext";
import {
  formatPrice,
  formatPricingPeriodInfo,
  trackMetaPixel,
} from "common/helpers/utils";
import type { Pricing } from "common/types";

const MAX_ACCOUNTS = 999;

export default function RentalSubscription({
  children,
  currentStep,
  onSuccess,
  pricing,
  minQuantity,
}: {
  children: (params: {
    isNextStepDisabled: boolean;
    isNextButtonLoading: boolean;
    onCreateSubscription: (paymentSourceId: string) => void;
    mainContent: ReactNode;
  }) => ReactNode;
  currentStep: "manage" | "summary" | "payment" | "cancelAccounts" | "success";
  onSuccess: () => void;
  pricing: { addon: Pricing; plan: Pricing };
  minQuantity: number;
}) {
  const { rental_subscription: currentSubscription } =
    useSelectedWorkspaceContext();

  let minAccounts =
    currentSubscription && currentSubscription.status === "active" ? 0 : 1;
  if (minQuantity) {
    minAccounts = Math.max(minAccounts, minQuantity);
  }

  const [accountCounts, setAccountsCount] = useState<number>(
    currentSubscription
      ? Math.max(minAccounts, currentSubscription.rented_count)
      : minAccounts,
  );

  const { estimateCreation, isLoadingEstimateCreation } =
    useWorkspaceEstimateCreation({
      pricing,
      quantity: accountCounts,
      subscription: currentSubscription,
    });
  const { estimateUpdate, isLoadingEstimateUpdate } =
    useWorkspaceEstimateUpdate({
      pricing,
      quantity: accountCounts,
      prorate: false,
      subscription: currentSubscription,
    });
  const {
    estimateUpdate: proRatedEstimateUpdate,
    isLoadingEstimateUpdate: isLoadingProratedEstimateUpdate,
  } = useWorkspaceEstimateUpdate({
    pricing,
    quantity: accountCounts,
    prorate: true,
    subscription: currentSubscription,
  });
  const { estimateRenewal, isLoadingEstimateRenewal } =
    useWorkspaceEstimateRenewal(currentSubscription);
  const { addRentalAccounts, isAddingRentalAccounts } = useRentalMutations();
  const { createSubscription, isCreatingSubscription } =
    useSubscriptionMutations();

  async function onCreateSubscription(paymentSourceId: string) {
    let subscriptionId: string;

    if (currentSubscription) {
      await addRentalAccounts({
        rentalCount: accountCounts,
        planPriceId: pricing.plan.period === 6 ? pricing.plan.id : undefined,
      });

      subscriptionId = currentSubscription.id;
    } else {
      const { id } = await createSubscription({
        count: accountCounts,
        type: "rental",
        planId: pricing.plan.id,
        paymentSourceId,
      });

      subscriptionId = id;
    }

    const eventData = {
      value: pricing.plan.price * accountCounts,
      currency: "USD",
      content_type: "subscription",
      content_ids: [subscriptionId],
      num_items: accountCounts,
    };

    trackMetaPixel("track", "Subscribe", eventData);
    trackMetaPixel("track", "Purchase", eventData);

    onSuccess();
  }

  const shouldShowCurrentInfo =
    currentSubscription &&
    currentSubscription.status !== "cancelled" &&
    currentSubscription.status !== "non_renewing";

  const estimate = estimateCreation ?? estimateUpdate;

  const isLoadingEstimate =
    isLoadingEstimateCreation || isLoadingEstimateUpdate;

  //all the limits below should not apply for subscriptions that are cancelled
  const isDowngrade =
    currentSubscription?.status === "active" &&
    accountCounts < currentSubscription.rented_count;

  const isUnchangedSubscription =
    currentSubscription?.status === "active"
      ? currentSubscription.rented_count === accountCounts &&
        currentSubscription.item_price.id === pricing.plan.id
      : false;

  let bannerComponent: JSX.Element;

  if (currentStep === "manage") {
    if (isDowngrade) {
      bannerComponent = (
        <Banner
          textNode="Outreach Avatars cannot be cancelled through the dashboard, please contact our team to downgrade your subscription"
          variant="warning"
          ctaComponent={
            <Button
              variant="secondary-black"
              size="sm"
              className="ml-auto"
              onClick={() => {}}
            >
              Contact support
            </Button>
          }
        />
      );
    } else if (
      pricing.plan.period_unit === "month" &&
      pricing.plan.period === 6 &&
      !isUnchangedSubscription
    ) {
      bannerComponent = (
        <Banner
          key={4}
          variant="gradient"
          textNode="You’re signing up for a 6-month plan. Future purchases and plan updates will follow this same 6-month billing cycle"
        />
      );
    }
  } else if (currentStep === "summary") {
    bannerComponent = (
      <Banner
        textNode="Outreach Avatars need some time to be prepared by our team and assigned to your account. They will show up in your dashboard within 2 business days"
        variant="warning"
      />
    );
  }

  const pricingDetailsContent = (
    <>
      {shouldShowCurrentInfo && (
        <>
          <div className="flex items-center justify-between px-2" key={3}>
            <span className="text-button-sm text-black-600">
              Current price per account
            </span>
            {isLoadingEstimateRenewal ? (
              <Skeleton className="h-5 w-28" />
            ) : (
              <span className="text-md-medium text-black-500">
                {formatPrice(estimateRenewal.pricePerItem / 100)}/
                {formatPricingPeriodInfo(currentSubscription.item_price)}
              </span>
            )}
          </div>
          <div className="flex items-center justify-between px-2" key={4}>
            <span className="text-button-sm text-black-600">
              Current no. of accounts
            </span>
            <span className="text-md-medium text-black-500">
              {currentSubscription.rented_count}
            </span>
          </div>
          <div className="flex items-center justify-between px-2" key={5}>
            <span className="text-button-sm text-black-600">Current price</span>
            {isLoadingEstimateRenewal ? (
              <Skeleton className="h-5 w-28" />
            ) : (
              <span className="text-md-medium text-black-500">
                {formatPrice(estimateRenewal.amountDue / 100)}/
                {formatPricingPeriodInfo(currentSubscription.item_price)}
              </span>
            )}
          </div>
          <div className="w-full h-px bg-black-200" key={6} />
        </>
      )}
      <div className="flex items-center justify-between px-2">
        <span className="text-button-sm text-black-600">
          New price per account
        </span>
        <span className="text-black-950 text-md-bold">
          {formatPrice(pricing.addon.price / 100)}/
          {formatPricingPeriodInfo(pricing.plan)}
        </span>
      </div>
      <div className="flex items-center justify-between px-2">
        <span className="text-button-sm text-black-600">
          New no. of accounts
        </span>
        <span className="text-black-950 text-md-bold">{accountCounts}</span>
      </div>
      <div className="flex items-center justify-between px-2">
        <span className="text-button-sm text-black-600">New price</span>
        {isLoadingEstimate ? (
          <Skeleton className="h-5 w-28" />
        ) : (
          <span className="text-black-950 text-md-bold">
            {formatPrice(estimate.amountDue / 100)}/
            {formatPricingPeriodInfo(pricing.plan)}
          </span>
        )}
      </div>
      {bannerComponent}
      <ToPayNow
        estimate={estimateCreation ? estimateCreation : proRatedEstimateUpdate}
        isLoading={isLoadingEstimateCreation || isLoadingProratedEstimateUpdate}
        isDowngradeOrEqual={isDowngrade || isUnchangedSubscription}
        isInTrial={false}
      />
    </>
  );

  const mainContent =
    currentStep === "manage" ? (
      <>
        <QuantitySelector
          quantity={accountCounts}
          setQuantity={setAccountsCount}
          minValue={minAccounts}
          maxValue={MAX_ACCOUNTS}
        />
        <div className="flex flex-col gap-y-2.5">{pricingDetailsContent}</div>
      </>
    ) : (
      pricingDetailsContent
    );

  return children({
    isNextStepDisabled: isDowngrade || isUnchangedSubscription,
    isNextButtonLoading: isAddingRentalAccounts || isCreatingSubscription,
    onCreateSubscription,
    mainContent,
  });
}
